import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from './ui/Logo';
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Search,
  ChevronDown,
  ChevronUp,
  Tags,
  Building,
  Briefcase,
  MapPin,
  BarChart3,
  MessageCircle,
  Activity,
  Users2,
  Star,
  PieChart,
  ClipboardCheck,
  BookOpen,
  Layers,
  Link as LinkIcon,
  Clock,
  Brain,
  Heart,
  Award,
  Zap,
  Rocket,
  Sparkles,
  Plug,
  ListChecks,
  UserX
} from 'lucide-react';

import { iconMap } from '../lib/iconRegistry';
import { hasGranularPermission } from '../lib/permissions';
import NestedMenu from './NestedMenu';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// Normaliza texto para busca: minúsculas e sem acentos/caracteres especiais
const normalizeSearchText = (text) => {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim();
};

// Resolve ícone que pode ser string (JSON) ou componente React (fallback legado)
const resolveIcon = (icon) => {
  if (!icon) return null;
  if (typeof icon === 'string') return iconMap[icon] || null;
  return icon;
};

const Navigation = ({ onCollapsedChange, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [menuSearch, setMenuSearch] = useState('');

  // Detecta viewport de celular para nunca exibir o menu em modo "só ícones" no celular
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // No celular o menu sempre aparece expandido (com rótulos); recolher é só no desktop
  const collapsed = desktopCollapsed && !isMobile;

  const handleCollapsedChange = (newCollapsed) => {
    setDesktopCollapsed(newCollapsed);
    if (onCollapsedChange) {
      onCollapsedChange(newCollapsed);
    }
  };

  // Fonte dos itens do menu: API > vazio
  const menuSource = useMemo(() => {
    if (user?.navigation?.items?.length > 0) return user.navigation.items;
    return [];
  }, [user?.navigation?.items]);

  // Auto-expandir menus laterais baseado na rota atual
  useEffect(() => {
    const currentPath = location.pathname;

    const expandParents = (items, path, parents = []) => {
      for (const item of items) {
        if (item.children) {
          const directChild = item.children.find(c => c.path === path);
          if (directChild) {
            const updates = {};
            parents.forEach(p => { updates[p] = true; });
            updates[item.label] = true;
            setExpandedMenus(prev => ({ ...prev, ...updates }));
            return true;
          }
          const found = expandParents(item.children, path, [...parents, item.label]);
          if (found) return true;
        }
      }
      return false;
    };

    expandParents(menuSource, currentPath);
  }, [location.pathname]);

  // Verifica se um item é acessível pelo usuário (novo sistema: permitted_ids)
  const hasAccessById = (id) => {
    if (!id) return false;
    if (isAdmin) return true;
    // Novo sistema: permitted_ids
    if (user?.permitted_ids?.length > 0) {
      return user.permitted_ids.includes(id);
    }
    return false;
  };

  // Filtra recursivamente itens do menu baseado em permissões
  const filterMenuItems = (items) => {
    if (!items || items.length === 0) return [];

    return items.reduce((acc, item) => {
      // Itens ocultos no menu (rotas de edição etc.) não aparecem na sidebar
      if (item.visibleInMenu === false) return acc;
      if (item.alwaysVisible) {
        acc.push({ ...item, children: item.children ? filterMenuItems(item.children) : undefined });
        return acc;
      }
      if (!isAdmin && item.adminOnly) return acc;

      const hasId = item.id && hasAccessById(item.id);
      const hasLegacy = item.permission && hasGranularPermission(user, item.permission);

      if (item.children) {
        const filteredChildren = filterMenuItems(item.children);
        if (filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren });
        }
        return acc;
      }

      // Se tem path (é rota), verificar permissão
      if (item.path) {
        if (hasId || hasLegacy) {
          acc.push(item);
        }
        return acc;
      }

      return acc;
    }, []);
  };

  // Filtra itens do menu baseado na busca textual
  const filterMenuBySearch = (items, query) => {
    if (!query.trim()) return items;
    const q = normalizeSearchText(query);

    return items.reduce((acc, item) => {
      const labelMatch = normalizeSearchText(item.label).includes(q);

      if (item.children) {
        const filteredChildren = filterMenuBySearch(item.children, q);
        if (filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren });
        } else if (labelMatch) {
          acc.push({ ...item, children: item.children });
        }
        return acc;
      }

      if (labelMatch) {
        acc.push(item);
      }
      return acc;
    }, []);
  };

  const navItems = useMemo(() => filterMenuItems(menuSource), [menuSource, user, isAdmin]);

  const displayedItems = useMemo(() => {
    if (!menuSearch.trim()) return navItems;
    return filterMenuBySearch(navItems, menuSearch);
  }, [navItems, menuSearch]);

  const toggleSubmenu = (label) => {
    // Allow submenu expansion even when collapsed for better UX
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div id="app-sidebar" role="navigation" className={`fixed left-0 top-0 h-full brand-sidebar text-white shadow-2xl transition-all duration-300 z-50 flex flex-col border-r border-white/10 w-64 ${
      collapsed ? 'md:w-16' : 'md:w-64'
    } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
      {/* Header com Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/10 flex-shrink-0">
        {collapsed ? (
          <Logo variant="icon" iconClassName="h-8 w-auto mx-auto" />
        ) : (
          <Logo variant="full" theme="dark" iconClassName="h-8 w-auto" wordClassName="text-lg" />
        )}
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleCollapsedChange(!collapsed)}
            className="hidden md:inline-flex text-white/80 hover:bg-white/20 p-1 rounded transition-colors"
          >
            {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Search input */}
      {!collapsed && (
        <div className="px-3 pt-3 pb-1 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/50" />
            <input
              type="text"
              placeholder="Pesquisar menu..."
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#00D4CB]/50 focus:border-transparent transition-all"
            />
          </div>
        </div>
      )}

      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent hover:scrollbar-thumb-white/30 min-h-0">
        <nav className="space-y-1 px-3">
          {displayedItems.map((item) => {
            const Icon = resolveIcon(item.icon);
            
            // Handle submenu items (itens com children são grupos)
            if (item.children) {
              const isExpanded = expandedMenus[item.label];
              const hasActiveChild = item.children?.some(child => 
                child.path && location.pathname === child.path
              );
              
              return (
                <div key={item.label}>
                  {/* Submenu Header */}
                  <button
                    onClick={() => toggleSubmenu(item.label)}
                    className={`group w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      hasActiveChild
                        ? 'bg-white/10 text-white shadow-lg backdrop-blur-sm ring-1 ring-white/10'
                        : 'text-white/70 hover:bg-white/10 hover:text-white hover:shadow-md'
                    }`}
                    title={item.label}
                  >
                    {Icon && <Icon className={`h-5 w-5 ${collapsed ? '' : 'mr-3'} transition-transform group-hover:scale-110`} />}
                    {!collapsed && (
                      <>
                        <span className="truncate flex-1 text-left">{item.label}</span>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 ml-2" />
                        ) : (
                          <ChevronDown className="h-4 w-4 ml-2" />
                        )}
                      </>
                    )}
                  </button>
                  
                  {/* Submenu Children */}
                  {(isExpanded || menuSearch.trim()) && item.children && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = resolveIcon(child.icon);
                        
                        // Handle ALL submenus with children using NestedMenu component
                        if (child.children && child.children.length > 0) {
                          return (
                            <NestedMenu
                              key={child.label}
                              item={child}
                              user={user}
                              isAdmin={isAdmin}
                              searchActive={!!menuSearch.trim()}
                            />
                          );
                        }
                        
                        // Handle regular submenu items  
                        if (child.path) {
                          const isActive = location.pathname === child.path;
                          
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                isActive
                                  ? 'bg-white/10 text-white shadow-lg backdrop-blur-sm border-l-2 border-[#00D4CB]'
                                  : 'text-white/60 hover:bg-white/10 hover:text-white hover:shadow-md'
                              }`}
                              title={child.label}
                            >
                              {ChildIcon && <ChildIcon className="h-4 w-4 mr-3 transition-transform group-hover:scale-110" />}
                              <span className="truncate">{child.label}</span>
                              {isActive && (
                                <div className="ml-auto w-2 h-2 bg-[#00D4CB] rounded-full"></div>
                              )}
                            </Link>
                          );
                        }
                        
                        return null;
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            // Handle regular menu items
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-white/10 text-white shadow-lg backdrop-blur-sm ring-1 ring-white/10'
                    : 'text-white/70 hover:bg-white/10 hover:text-white hover:shadow-md'
                }`}
                title={item.label}
              >
                {Icon && <Icon className={`h-5 w-5 ${collapsed ? '' : 'mr-3'} transition-transform group-hover:scale-110`} />}
                {!collapsed && (
                  <span className="truncate">{item.label}</span>
                )}
                {isActive && !collapsed && (
                  <div className="ml-auto w-2 h-2 bg-[#00D4CB] rounded-full"></div>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* User Info na parte inferior */}
      <div className="border-t border-white/20 p-4 flex-shrink-0">
        <Link
          to="/meu-perfil"
          className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'} mb-3 hover:bg-white/10 rounded-lg p-1 transition-colors ${collapsed ? '' : '-mx-1'}`}
          title={collapsed ? user?.name || 'Meu Perfil' : ''}
        >
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <Badge 
                variant={isAdmin ? 'default' : 'secondary'} 
                className={`text-xs mt-1 ${
                  isAdmin 
                    ? 'brand-gradient text-white border-0' 
                    : 'bg-white/15 text-white border-white/20'
                }`}
              >
                {isAdmin ? 'Admin' : 'Usuário'}
              </Badge>
            </div>
          )}
        </Link>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className={`w-full text-white/80 hover:bg-red-500 hover:text-white transition-all duration-200 ${
            collapsed ? 'px-2' : 'justify-start'
          }`}
          title={collapsed ? 'Sair' : ''}
        >
          <LogOut className={`h-4 w-4 ${collapsed ? '' : 'mr-2'}`} />
          {!collapsed && <span>Sair</span>}
        </Button>
      </div>
    </div>
  );
};

export default Navigation;
